import { z } from "zod";
import { isUtcInstant } from "../migrations/types.ts";
import {
  vocabularySnapshotV2ExerciseSetSchema,
  vocabularySnapshotV2MeaningCardSchema,
  vocabularySnapshotV2MeaningRelationSchema,
  vocabularySnapshotV2PracticeStateSchema,
  vocabularySnapshotV2ReviewEventSchema,
  vocabularySnapshotV2SchedulerStateSchema,
  vocabularySnapshotV2StageSchema,
  vocabularySnapshotV2WordEntrySchema,
  vocabularySnapshotV2WordTypeSchema,
} from "./v2.ts";

const utcInstantSchema = z.string().refine(isUtcInstant, {
  message: "Must be a UTC instant",
});

const V3_CARD_POLICY_VERSION = 1;

export const vocabularySnapshotV3WordTypeSchema = vocabularySnapshotV2WordTypeSchema;
export const vocabularySnapshotV3WordEntrySchema = vocabularySnapshotV2WordEntrySchema;
export const vocabularySnapshotV3MeaningCardSchema = vocabularySnapshotV2MeaningCardSchema;
export const vocabularySnapshotV3MeaningRelationSchema = vocabularySnapshotV2MeaningRelationSchema;
export const vocabularySnapshotV3ExerciseSetSchema = vocabularySnapshotV2ExerciseSetSchema;

export const vocabularySnapshotV3CardScheduleSchema = z.object({
  stage: vocabularySnapshotV2StageSchema,
  dueAt: utcInstantSchema,
  lastAssessmentAt: utcInstantSchema.nullable(),
  lastPresentedAt: utcInstantSchema.nullable(),
  presentationOrdinal: z.number().int().nonnegative().nullable(),
});

export const vocabularySnapshotV3PracticeStateSchema = vocabularySnapshotV2PracticeStateSchema.extend({
  cardSchedules: z.object({
    hide_meaning: vocabularySnapshotV3CardScheduleSchema,
    hide_word: vocabularySnapshotV3CardScheduleSchema,
  }),
});

export const vocabularySnapshotV3SchedulerStateSchema = vocabularySnapshotV2SchedulerStateSchema.extend({
  cardPolicyVersion: z.literal(V3_CARD_POLICY_VERSION),
  cardPresentationCounters: z.object({
    hide_meaning: z.number().int().nonnegative(),
    hide_word: z.number().int().nonnegative(),
  }),
  lastPresentedCardMeaningIds: z.object({
    hide_meaning: z.string().min(1).nullable(),
    hide_word: z.string().min(1).nullable(),
  }),
});

export const vocabularySnapshotV3CardTargetSchema = z.object({
  stage: vocabularySnapshotV2StageSchema,
  dueAt: utcInstantSchema,
  lastAssessmentAt: utcInstantSchema.nullable(),
  lastPresentedAt: utcInstantSchema.nullable(),
  presentationOrdinal: z.number().int().nonnegative().nullable(),
  lastExposureAt: utcInstantSchema.nullable(),
  stateRevision: z.number().int().nonnegative(),
});

export const vocabularySnapshotV3WrittenReviewEventSchema = vocabularySnapshotV2ReviewEventSchema.extend({
  format: z.literal("written"),
  origin: z.enum(["exercise", "legacy", "unspecified"]),
});

export const vocabularySnapshotV3CardReviewEventSchema = z.object({
  format: z.literal("cards"),
  eventId: z.string().min(1),
  sessionId: z.string().min(1),
  attemptId: z.string().min(1),
  meaningId: z.string().min(1),
  exerciseId: z.null(),
  exerciseKind: z.null(),
  direction: z.enum(["hide_meaning", "hide_word"]),
  actionKind: z.enum(["presentation", "reveal", "context", "grade", "skip", "revisit"]),
  gradingMethod: z.literal("self").nullable(),
  contextUsed: z.boolean(),
  meaningContentRevision: z.number().int().nonnegative(),
  expectedStateRevision: z.number().int().nonnegative(),
  presentedAt: utcInstantSchema,
  actionAt: utcInstantSchema,
  previousActionId: z.string().min(1).nullable(),
  previousActionAt: utcInstantSchema.nullable(),
  knownObservedAt: utcInstantSchema.nullable(),
  outcome: z.enum(["correct", "incorrect"]).nullable(),
  wasDue: z.boolean(),
  wasSpaced: z.boolean(),
  isFirstAssessment: z.boolean(),
  scheduledOrExtra: z.enum(["scheduled", "extra"]),
  cardPolicyVersion: z.literal(V3_CARD_POLICY_VERSION),
  previousTarget: vocabularySnapshotV3CardTargetSchema,
  resultingTarget: vocabularySnapshotV3CardTargetSchema,
});

export const vocabularySnapshotV3ReviewEventSchema = z.discriminatedUnion("format", [
  vocabularySnapshotV3WrittenReviewEventSchema,
  vocabularySnapshotV3CardReviewEventSchema,
]);

export const vocabularySnapshotV3Schema = z
  .object({
    schemaVersion: z.literal(3),
    dataRevision: z.number().int().nonnegative(),
    schedulerState: vocabularySnapshotV3SchedulerStateSchema,
    words: z.array(vocabularySnapshotV3WordEntrySchema),
    meanings: z.array(vocabularySnapshotV3MeaningCardSchema),
    relations: z.array(vocabularySnapshotV3MeaningRelationSchema),
    exerciseSets: z.array(vocabularySnapshotV3ExerciseSetSchema),
    practiceStates: z.array(vocabularySnapshotV3PracticeStateSchema),
    reviewHistory: z.array(vocabularySnapshotV3ReviewEventSchema),
    historyTruncated: z.boolean(),
  })
  .strict()
  .superRefine((snapshot, ctx) => {
    const meaningIds = new Set(snapshot.meanings.map((meaning) => meaning.id));
    const wordIds = new Set(snapshot.words.map((word) => word.id));
    const practiceIds = snapshot.practiceStates.map((state) => state.meaningId);
    if (new Set(practiceIds).size !== practiceIds.length) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["practiceStates"],
        message: "Practice states must be unique per meaning.",
      });
    }
    for (const meaning of snapshot.meanings) {
      if (!wordIds.has(meaning.wordId)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["meanings"],
          message: `Meaning ${meaning.id} references missing word ${meaning.wordId}.`,
        });
      }
    }
    for (const state of snapshot.practiceStates) {
      if (!meaningIds.has(state.meaningId)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["practiceStates"],
          message: `Practice state refers to missing meaning ${state.meaningId}.`,
        });
      }
    }
    if (
      snapshot.schedulerState.lastPracticedMeaningId &&
      !meaningIds.has(snapshot.schedulerState.lastPracticedMeaningId)
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["schedulerState", "lastPracticedMeaningId"],
        message: "lastPracticedMeaningId must reference a saved meaning.",
      });
    }
    for (const direction of ["hide_meaning", "hide_word"] as const) {
      const meaningId = snapshot.schedulerState.lastPresentedCardMeaningIds[direction];
      if (meaningId && !meaningIds.has(meaningId)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["schedulerState", "lastPresentedCardMeaningIds", direction],
          message: "lastPresentedCardMeaningIds must reference a saved meaning.",
        });
      }
    }
    const eventIds = snapshot.reviewHistory.map((event) => event.eventId);
    if (new Set(eventIds).size !== eventIds.length) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["reviewHistory"],
        message: "Review event IDs must be unique.",
      });
    }
    for (const event of snapshot.reviewHistory) {
      if (event.format !== "cards") {
        continue;
      }
      if (event.actionKind === "grade") {
        if (event.outcome == null || event.gradingMethod !== "self") {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["reviewHistory"],
            message: "A card grade is a self-check with a correct or incorrect outcome.",
          });
        }
      } else if (event.outcome != null || event.gradingMethod != null) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["reviewHistory"],
          message: "Only a card grade has a self-check outcome.",
        });
      }
    }
  });

export type VocabularySnapshotV3 = z.infer<typeof vocabularySnapshotV3Schema>;
